/* eslint-disable no-unused-vars */
import AsyncStorage from '@react-native-async-storage/async-storage';

const BASE_URL = 'https://api.jobeng.ai';

class LearningApiService {
  constructor() {
    this.baseURL = BASE_URL;
  }

  // Helper method to get auth token
  async getAuthToken() {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      return token;
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  }

  // Helper method to make authenticated requests
  async makeRequest(endpoint, options = {}) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}${endpoint}`;
      
      const defaultHeaders = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };

      if (token) {
        defaultHeaders['Authorization'] = `Bearer ${token}`;
      }

      const config = {
        ...options,
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
      };

      console.log(`Making request to: ${url}`, config);
      
      const response = await fetch(url, config);
      const responseText = await response.text();
      
      console.log(`Response status: ${response.status}`);
      console.log(`Response text: ${responseText}`);

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        throw new Error('Invalid response format from server');
      }

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Authentication APIs
  async registerLearningUser(userData) {
    return this.makeRequest('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        ...userData,
        user_type: 'professional', // Backend workaround
        profile_type: 'learning',
        is_learning_user: true,
      }),
    });
  }

  async loginLearningUser(credentials) {
    return this.makeRequest('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async getUserProfile() {
    return this.makeRequest('/auth/profile');
  }

  async updateUserProfile(profileData) {
    return this.makeRequest('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  // Dashboard APIs
  async getDashboardData() {
    try {
      // For now, return mock data since backend doesn't have learning endpoints yet
      return {
        success: true,
        data: {
          totalCourses: 12,
          completedCourses: 3,
          inProgressCourses: 2,
          totalHours: 45,
          currentCourses: [
            {
              id: 1,
              title: 'Civil Engineering Fundamentals',
              progress: 65,
              instructor: 'Dr. Sarah Johnson, CEng',
              duration: '8 weeks',
              nextLesson: 'Structural Analysis Basics',
              thumbnail: 'https://via.placeholder.com/300x200/3b82f6/white?text=Civil+Engineering'
            },
            {
              id: 2,
              title: 'AutoCAD for Engineers',
              progress: 40,
              instructor: 'Mark Thompson, P.E.',
              duration: '6 weeks',
              nextLesson: '3D Modeling Techniques',
              thumbnail: 'https://via.placeholder.com/300x200/10b981/white?text=AutoCAD'
            }
          ],
          recentAchievements: [
            {
              id: 1,
              title: 'Course Completed',
              description: 'Project Management Essentials',
              date: '2 days ago',
              icon: 'trophy-outline'
            },
            {
              id: 2,
              title: 'Certificate Earned',
              description: 'Health & Safety Fundamentals',
              date: '1 week ago',
              icon: 'ribbon-outline'
            }
          ],
          upcomingDeadlines: [
            {
              id: 1,
              title: 'Assignment Due',
              course: 'Civil Engineering Fundamentals',
              date: 'Tomorrow',
              priority: 'high'
            },
            {
              id: 2,
              title: 'Quiz Available',
              course: 'AutoCAD for Engineers',
              date: 'In 3 days',
              priority: 'medium'
            }
          ],
          learningStats: {
            weeklyProgress: 8,
            monthlyGoal: 20,
            streak: 5,
          },
        }
      };
    } catch (error) {
      console.error('Error getting dashboard data:', error);
      throw error;
    }
  }

  // Course APIs
  async getAllCourses(filters = {}) {
    try {
      // Mock data for courses
      const allCourses = [
        {
          id: 1,
          title: 'Civil Engineering Fundamentals',
          category: 'Civil',
          instructor: 'Dr. Sarah Johnson, CEng',
          duration: '8 weeks',
          level: 'Beginner',
          rating: 4.8,
          students: 1250,
          price: 'Free',
          thumbnail: 'https://via.placeholder.com/300x200/3b82f6/white?text=Civil+Engineering',
          description: 'Learn the fundamentals of civil engineering including structural analysis, materials, and design principles.',
          skills: ['Structural Analysis', 'Material Science', 'Design Principles', 'Safety Standards']
        },
        {
          id: 2,
          title: 'AutoCAD for Engineers',
          category: 'Computer',
          instructor: 'Mark Thompson, P.E.',
          duration: '6 weeks',
          level: 'Intermediate',
          rating: 4.7,
          students: 980,
          price: 'Free',
          thumbnail: 'https://via.placeholder.com/300x200/10b981/white?text=AutoCAD',
          description: 'Master AutoCAD for engineering design and drafting with hands-on projects.',
          skills: ['2D Drafting', '3D Modeling', 'Technical Drawing', 'Design Documentation']
        },
        {
          id: 3,
          title: 'Mechanical Design Principles',
          category: 'Mechanical',
          instructor: 'Prof. James Wilson',
          duration: '10 weeks',
          level: 'Advanced',
          rating: 4.9,
          students: 750,
          price: 'Free',
          thumbnail: 'https://via.placeholder.com/300x200/f59e0b/white?text=Mechanical+Design',
          description: 'Advanced mechanical design concepts including thermodynamics, fluid mechanics, and machine design.',
          skills: ['Thermodynamics', 'Fluid Mechanics', 'Machine Design', 'CAD Modeling']
        },
        {
          id: 4,
          title: 'Electrical Circuit Analysis',
          category: 'Electrical',
          instructor: 'Dr. Emily Chen',
          duration: '7 weeks',
          level: 'Beginner',
          rating: 4.6,
          students: 1100,
          price: 'Free',
          thumbnail: 'https://via.placeholder.com/300x200/ef4444/white?text=Electrical+Circuits',
          description: 'Understand electrical circuits, Ohm\'s law, and basic electrical engineering principles.',
          skills: ['Circuit Analysis', 'Ohm\'s Law', 'AC/DC Circuits', 'Electrical Safety']
        },
        {
          id: 5,
          title: 'Project Management for Engineers',
          category: 'Management',
          instructor: 'Sarah Mitchell, PMP',
          duration: '5 weeks',
          level: 'Intermediate',
          rating: 4.8,
          students: 890,
          price: 'Free',
          thumbnail: 'https://via.placeholder.com/300x200/8b5cf6/white?text=Project+Management',
          description: 'Learn project management methodologies specifically tailored for engineering projects.',
          skills: ['Project Planning', 'Risk Management', 'Team Leadership', 'Budget Control']
        }
      ];

      // Apply filters
      let filteredCourses = allCourses;
      
      if (filters.category && filters.category !== 'All') {
        filteredCourses = filteredCourses.filter(course => course.category === filters.category);
      }
      
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        filteredCourses = filteredCourses.filter(course => 
          course.title.toLowerCase().includes(searchTerm) ||
          course.description.toLowerCase().includes(searchTerm) ||
          course.instructor.toLowerCase().includes(searchTerm)
        );
      }

      return {
        success: true,
        data: filteredCourses
      };
    } catch (error) {
      console.error('Error getting courses:', error);
      throw error;
    }
  }

  async getCourseDetails(courseId) {
    try {
      const coursesResponse = await this.getAllCourses();
      const course = coursesResponse.data.find(c => c.id === parseInt(courseId));
      
      if (!course) {
        throw new Error('Course not found');
      }

      // Add additional details
      const courseDetails = {
        ...course,
        modules: [
          {
            id: 1,
            title: 'Introduction and Fundamentals',
            lessons: 8,
            duration: '2 hours',
            completed: true
          },
          {
            id: 2,
            title: 'Core Concepts',
            lessons: 12,
            duration: '3.5 hours',
            completed: false
          },
          {
            id: 3,
            title: 'Practical Applications',
            lessons: 10,
            duration: '4 hours',
            completed: false
          },
          {
            id: 4,
            title: 'Advanced Topics',
            lessons: 6,
            duration: '2.5 hours',
            completed: false
          }
        ],
        prerequisites: ['Basic Mathematics', 'Physics Fundamentals'],
        outcomes: [
          'Understand core engineering principles',
          'Apply theoretical knowledge to practical problems',
          'Complete real-world engineering projects',
          'Prepare for professional certification'
        ]
      };

      return {
        success: true,
        data: courseDetails
      };
    } catch (error) {
      console.error('Error getting course details:', error);
      throw error;
    }
  }

  async enrollInCourse(courseId) {
    try {
      // Mock enrollment
      console.log(`Enrolling in course ${courseId}`);
      return {
        success: true,
        message: 'Successfully enrolled in course'
      };
    } catch (error) {
      console.error('Error enrolling in course:', error);
      throw error;
    }
  }

  async getMyCourses() {
    try {
      // Mock user's enrolled courses
      return {
        success: true,
        data: [
          {
            id: 1,
            title: 'Civil Engineering Fundamentals',
            progress: 65,
            instructor: 'Dr. Sarah Johnson, CEng',
            nextLesson: 'Structural Analysis Basics',
            thumbnail: 'https://via.placeholder.com/300x200/3b82f6/white?text=Civil+Engineering'
          },
          {
            id: 2,
            title: 'AutoCAD for Engineers',
            progress: 40,
            instructor: 'Mark Thompson, P.E.',
            nextLesson: '3D Modeling Techniques',
            thumbnail: 'https://via.placeholder.com/300x200/10b981/white?text=AutoCAD'
          }
        ]
      };
    } catch (error) {
      console.error('Error getting my courses:', error);
      throw error;
    }
  }

  async updateCourseProgress(courseId, progress) {
    try {
      console.log(`Updating course ${courseId} progress to ${progress}%`);
      return {
        success: true,
        message: 'Progress updated successfully'
      };
    } catch (error) {
      console.error('Error updating course progress:', error);
      throw error;
    }
  }

  // Books APIs
  async getEngineeringBooks(filters = {}) {
    try {
      const allBooks = [
        {
          id: 1,
          title: 'Structural Engineering Handbook',
          author: 'Dr. Michael Brown',
          category: 'Civil Engineering',
          pages: 850,
          rating: 4.8,
          downloads: 2500,
          thumbnail: 'https://via.placeholder.com/200x300/3b82f6/white?text=Structural+Handbook',
          description: 'Comprehensive guide to structural engineering principles and practices.',
          topics: ['Steel Design', 'Concrete Structures', 'Foundation Design', 'Seismic Analysis']
        },
        {
          id: 2,
          title: 'Mechanical Engineering Design',
          author: 'Prof. Lisa Anderson',
          category: 'Mechanical Engineering',
          pages: 720,
          rating: 4.7,
          downloads: 1800,
          thumbnail: 'https://via.placeholder.com/200x300/10b981/white?text=Mechanical+Design',
          description: 'Essential mechanical engineering design concepts and methodologies.',
          topics: ['Machine Elements', 'Material Selection', 'Failure Analysis', 'Design Optimization']
        },
        {
          id: 3,
          title: 'Electrical Power Systems',
          author: 'Dr. Robert Kim',
          category: 'Electrical Engineering',
          pages: 650,
          rating: 4.9,
          downloads: 2100,
          thumbnail: 'https://via.placeholder.com/200x300/ef4444/white?text=Power+Systems',
          description: 'Modern electrical power systems analysis and design.',
          topics: ['Power Generation', 'Transmission Lines', 'Protection Systems', 'Smart Grids']
        }
      ];

      // Apply filters
      let filteredBooks = allBooks;
      
      if (filters.category && filters.category !== 'All') {
        filteredBooks = filteredBooks.filter(book => book.category === filters.category);
      }
      
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        filteredBooks = filteredBooks.filter(book => 
          book.title.toLowerCase().includes(searchTerm) ||
          book.author.toLowerCase().includes(searchTerm) ||
          book.description.toLowerCase().includes(searchTerm)
        );
      }

      return {
        success: true,
        data: filteredBooks
      };
    } catch (error) {
      console.error('Error getting books:', error);
      throw error;
    }
  }

  async getBookDetails(bookId) {
    try {
      const booksResponse = await this.getEngineeringBooks();
      const book = booksResponse.data.find(b => b.id === parseInt(bookId));
      
      if (!book) {
        throw new Error('Book not found');
      }

      return {
        success: true,
        data: book
      };
    } catch (error) {
      console.error('Error getting book details:', error);
      throw error;
    }
  }

  // Virtual Internships APIs
  async getVirtualInternships(filters = {}) {
    try {
      const allInternships = [
        {
          id: 1,
          title: 'Civil Engineering Project Assistant',
          company: 'Arup Engineering',
          location: 'Remote - UK',
          duration: '12 weeks',
          type: 'Virtual',
          level: 'Entry Level',
          applications: 45,
          deadline: '2024-02-15',
          thumbnail: 'https://via.placeholder.com/300x200/3b82f6/white?text=Arup+Internship',
          description: 'Work on real civil engineering projects with experienced engineers.',
          requirements: ['Engineering Student', 'AutoCAD Knowledge', 'Strong Communication'],
          benefits: ['Mentorship', 'Certificate', 'Portfolio Projects', 'Networking']
        },
        {
          id: 2,
          title: 'Mechanical Design Intern',
          company: 'Rolls-Royce',
          location: 'Remote - UK',
          duration: '16 weeks',
          type: 'Virtual',
          level: 'Intermediate',
          applications: 32,
          deadline: '2024-02-20',
          thumbnail: 'https://via.placeholder.com/300x200/10b981/white?text=Rolls+Royce',
          description: 'Contribute to aerospace engineering design projects.',
          requirements: ['Mechanical Engineering', 'CAD Software', '3D Modeling'],
          benefits: ['Industry Experience', 'Professional References', 'Skill Development']
        }
      ];

      return {
        success: true,
        data: allInternships
      };
    } catch (error) {
      console.error('Error getting internships:', error);
      throw error;
    }
  }

  async applyForInternship(internshipId) {
    try {
      console.log(`Applying for internship ${internshipId}`);
      return {
        success: true,
        message: 'Application submitted successfully'
      };
    } catch (error) {
      console.error('Error applying for internship:', error);
      throw error;
    }
  }

  // Search APIs
  async searchContent(query, filters = {}) {
    try {
      const [coursesResponse, booksResponse, internshipsResponse] = await Promise.all([
        this.getAllCourses({ search: query }),
        this.getEngineeringBooks({ search: query }),
        this.getVirtualInternships({ search: query })
      ]);

      return {
        success: true,
        data: {
          courses: coursesResponse.data,
          books: booksResponse.data,
          internships: internshipsResponse.data
        }
      };
    } catch (error) {
      console.error('Error searching content:', error);
      throw error;
    }
  }

  // Learning Statistics APIs
  async getLearningStats() {
    try {
      return {
        success: true,
        data: {
          totalHours: 45,
          coursesCompleted: 3,
          certificatesEarned: 2,
          currentStreak: 5,
          weeklyGoal: 10,
          weeklyProgress: 8,
          monthlyGoal: 40,
          monthlyProgress: 32
        }
      };
    } catch (error) {
      console.error('Error getting learning stats:', error);
      throw error;
    }
  }

  async getAchievements() {
    try {
      return {
        success: true,
        data: [
          {
            id: 1,
            title: 'First Course Completed',
            description: 'Completed your first engineering course',
            icon: 'trophy-outline',
            earned: true,
            date: '2024-01-15'
          },
          {
            id: 2,
            title: 'Week Streak',
            description: 'Maintained a 7-day learning streak',
            icon: 'flame-outline',
            earned: false,
            progress: 5
          }
        ]
      };
    } catch (error) {
      console.error('Error getting achievements:', error);
      throw error;
    }
  }
}

export default new LearningApiService();
